import { cwd } from 'node:process';
import React from 'react';
import type { ReactElement } from 'react';
import { SiteAuthProvider } from '@wix/cli-auth';
import type { UserInfo } from '@wix/cli-auth';
import { render } from '@wix/cli-ui-kit';
import { I18nProvider } from '@wix/cli-i18n';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import { BiProvider } from '@wix/cli-telemetry-react';
import { addUserFields, reportCommandStartEvent } from '@wix/cli-telemetry';
import type { CommandServices } from '@wix/cli-command-contract';
import type { Command } from '@commander-js/extra-typings';
import type { ProjectModel } from '../model.js';
import { ProjectModelProvider, loadProjectModel } from '../model.js';
import messages from '../messages.json' with { type: 'json' };

async function initServices(
  { metaSiteId }: ProjectModel,
  { biLogger, errorReporter }: CommandServices
) {
  errorReporter.setContext('site info', {
    siteId: metaSiteId,
  });

  biLogger.updateDefaults({
    _msid: metaSiteId,
  });
}

export async function renderCommand(
  command: Command<unknown[]>,
  services: CommandServices,
  cliVersion: string,
  component: ReactElement
) {
  await runCommand(command, services, cliVersion, async (model) => {
    const { biLogger, errorReporter } = services;

    const handleLogin = (userInfo: UserInfo) => {
      errorReporter.setUser({ id: userInfo.userId });
      addUserFields(biLogger, userInfo.userId);
    };

    await render(
      <BiProvider value={biLogger}>
        <ErrorReporterProvider value={errorReporter}>
          <SiteAuthProvider siteId={model.metaSiteId} onLogin={handleLogin}>
            <I18nProvider messages={messages}>
              <ProjectModelProvider value={model}>
                {component}
              </ProjectModelProvider>
            </I18nProvider>
          </SiteAuthProvider>
        </ErrorReporterProvider>
      </BiProvider>
    );
  });
}

async function runCommand(
  command: Command<unknown[]>,
  services: CommandServices,
  cliVersion: string,
  commandFn: (model: ProjectModel) => Promise<void>
) {
  const model = await loadProjectModel(cwd());

  await initServices(model, services);

  const commandStatus = reportCommandStartEvent({
    biLogger: services.biLogger,
    command,
    cliVersion,
    flow: 'Sites_V2',
  });

  try {
    await commandFn(model);
    commandStatus.succeed();
  } catch (e) {
    commandStatus.failed(e);
    throw e;
  }
}
